import { zodResolver } from "@hookform/resolvers/zod";
import React, { useEffect, useState } from "react";
import {
  Button,
  Col,
  Container,
  Form,
  Row,
  Placeholder,
} from "react-bootstrap";
import { Controller, useForm } from "react-hook-form";
import { Link, useLocation, useNavigate, useParams } from "react-router-dom";
import { z } from "zod";
import PhoneInput from "react-phone-input-2";
import Loader from "../../../../components/Common/Loader";
import { catchAsync, checkResponse } from "../../../../utilities/utilities";
import {
  ESSENTAILLIST,
  HOTELADMIN,
  HOTELCREATE,
  HOTELDETAILS,
  HOTELUPDATE,
  PINLISTAPI,
  ROOMESSENTAILLIST,
} from "../../../../services/ApiCalls";
import Select from "react-select";
import GoogleAutocomplete from "../../../../components/Common/GoogleAutoComplete";
import { Country, State, City } from "country-state-city";
import fileUploader from "../../../../utilities/fileUploader";
import Loading from "../../../../components/Common/Loading";
import { useTranslation } from "react-i18next";
const schema = z.object({
  hotelName: z.string().min(1, { message: "Hotel Name is required" }),
  address: z.string().min(1, { message: "Address is required" }),
  country: z.string().min(1, { message: "Country is required" }),
  state: z.string().min(1, { message: "State is required" }),
  city: z.string().min(1, { message: "City is required" }),
  totalRoom: z
    .number({ invalid_type_error: "Total rooms must be a number" })
    .min(0, { message: "Total rooms cannot be negative" }),
  images: z
    .array(z.string())
    .min(1, { message: "At least one image is required" }),
  mobileNumber: z
    .string()
    .min(7, { message: "Mobile number is required" })
    .regex(/^[0-9]+$/, { message: "Only numbers allowed" }),
  description: z.string().optional(),
  email: z.string().email({ message: "Invalid email address" }),
  status: z.boolean(),
  countryCode: z.string().optional(),
  userId: z.string().min(1, { message: "Hotel Admin is required" }),
  amenities: z.array(z.string()).optional(),
  pinStatus: z
    .object({
      label: z.string(),
      priority: z.number(),
    })
    .nullable()
    .optional(),
  // checkInTime: z.string().optional(),
  // checkOutTime: z.string().optional(),
});

const AddEditHotel = () => {
  const [loader, setLoader] = useState(false);
  const [hotelData, setHotelData] = useState();
  const [hotelAdmins, setHotelAdmins] = useState([]);
  const { id } = useParams();
  const location = useLocation();

  const queryParams = new URLSearchParams(location.search);
  const page = queryParams.get("page") || 1;
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [hotelAmenities, setHotelAmenities] = useState([]);
  const [roomAmenities, setRoomAmenities] = useState([]);
  const [hotelAdminData, setHotelAdminData] = useState([]);
  const [imgloader, setImgLoader] = useState(false);
  const [pinListData, setPinListData] = useState();
  const [imagePreview, setImagePreview] = useState();

  const {
    register,
    handleSubmit,
    watch,
    setValue,
    control,
    formState: { errors },
    reset,
  } = useForm({
    resolver: zodResolver(schema),
    defaultValues: {
      hotelName: "",
      totalRoom: "",
      country: "",
      state: "",
      city: "",
      address: "",
      mobileNumber: "",
      description: "",
      email: "",
      status: false,
      countryCode: "",
      userId: "",
      amenities: [],
      pinStatus: {},
      checkInTime: "",
      checkOutTime: "",
    },
  });

  const getAmenityData = catchAsync(async () => {
    const [roomRes, hotelRes, hotelAdminRes, pinRes] = await Promise.all([
      ROOMESSENTAILLIST({ limit: 100, page: 1, status: "Active" }),
      ESSENTAILLIST({ limit: 100, page: 1, status: "Active" }),
      HOTELADMIN({ limit: 100, page: 1, status: "Active" }),
      PINLISTAPI(),
    ]);

    checkResponse({ res: roomRes, setData: setRoomAmenities });
    checkResponse({ res: hotelRes, setData: setHotelAmenities });
    checkResponse({ res: hotelAdminRes, setData: setHotelAdminData });
    checkResponse({ res: pinRes, setData: setPinListData });
  });

  const submitHandler = catchAsync(async (data) => {
    const body = {
      ...data,
      status: data.status ? "Active" : "Inactive",
      hotelImages:data.images,

      totalRoom: Number(data?.totalRoom),
      location: {
        type: "Point",
        coordinates: [Number(watch("lng")), Number(watch("lat"))],
      },
      ...(id && { _id: id }),
    };

    const res = await (id ? HOTELUPDATE(body) : HOTELCREATE(body));
    const success = checkResponse({ res, showSuccess: true });
    if (success) navigate(`/hotel?page=${page}`);
  });

  const getData = catchAsync(async () => {
    setLoader(true);
    const res = await HOTELDETAILS(id);

    const success = checkResponse({ res, setData: setHotelData, setLoader });
    if (success && res?.data?.data) {
      const data = res.data.data;
      setImagePreviews(data.hotelImages.map((img)=>img.link) || []);
      reset({
        hotelName: data.hotelName || "",
        address: data.address || "",
        mobileNumber: data.mobileNumber || "",
        description: data.description || "",
        country: data?.country || "",
        state: data?.state || "",
        city: data?.city || "",
        totalRoom: data?.totalRoom?.toString() || "",
        email: data.email || "",
        status: data.status?.toLowerCase() === "active",
        countryCode: data.countryCode?.replace("+", "") || "",
        lat: data.location?.coordinates?.[1] || "",
        lng: data.location?.coordinates?.[0] || "",
        checkInTime: data?.checkInTime || "",
        checkOutTime: data?.checkOutTime || "",
        userId: data.userId?._id || "",
        amenities: data.amenities?.map((a) => a._id) || [],
        images: data.hotelImages?.map((item) => item._id) || [],
        pinStatus: data.pinStatus ? JSON.stringify(data.pinStatus) : "",
      });
    }
  }, setLoader);

  useEffect(() => {
    getAmenityData();
    if (id) getData();
  }, [id]);

  const addressChangeHandler = (address) => {
    setValue("address", address?.address, { shouldValidate: true });
    setValue("lat", address?.lat, { shouldValidate: true });
    setValue("lng", address?.lng, { shouldValidate: true });
  };

  const handleCheckbox = (name, value, checked) => {
    const currentValues = watch(name) || [];
    if (checked) {
      setValue(name, [...currentValues, value]);
    } else {
      setValue(
        name,
        currentValues.filter((item) => item !== value)
      );
    }
  };
  // const countryOptions = Country.getAllCountries().map((c) => ({
  //   value: c.isoCode,
  //   label: c.name,
  // }));

  // const stateOptions = (countryCode) =>
  //   State.getStatesOfCountry(countryCode).map((s) => ({
  //     value: s.isoCode,
  //     label: s.name,
  //   }));

  // const cityOptions = (countryCode, stateCode) =>
  //   City.getCitiesOfState(countryCode, stateCode).map((ci) => ({
  //     value: ci.name,
  //     label: ci.name,
  //   }));

  const countryOptions = Country.getAllCountries().filter((c) => ["Thailand", "Vietnam"].includes(c.name)).map((c) => ({
    value: c.name, // send name instead of isoCode
    label: c.name
  }));

  const stateOptions = (countryName) =>
    State.getStatesOfCountry(
      Country.getAllCountries().find((c) => c.name === countryName)?.isoCode
    ).map((s) => ({
      value: s.name, // send name
      label: s.name,
    }));

  const cityOptions = (countryName, stateName) => {
    const country = Country.getAllCountries().find(
      (c) => c.name === countryName
    );
    const state = State.getStatesOfCountry(country?.isoCode).find(
      (s) => s.name === stateName
    );
    return City.getCitiesOfState(country?.isoCode, state?.isoCode).map(
      (ci) => ({
        value: ci.name, // send name
        label: ci.name,
      })
    );
  };

  const [imagePreviews, setImagePreviews] = useState([]);
  const handleImageChange = catchAsync(async (e) => {
    setImgLoader(true);
    const files = Array.from(e.target.files);

    const uploadedUrls = [];
    const uploadedIds = [];
    for (let file of files) {
      const res = await fileUploader(file);
      console.log(res,"reeeeeeeeeeeee")
      if (res?.link) {
        uploadedUrls.push(res.link);
        uploadedIds.push(res._id)
      }
    }
    const updated = [...(watch("images") || []), ...uploadedIds];
    const updatedUrl = [...(imagePreviews|| []), ...uploadedUrls];
    setValue("images", updated, { shouldValidate: true });
    setImagePreviews(updatedUrl);
    setImgLoader(false);
  });

  const handleRemoveImage = (index) => {
    const updated = (watch("images") || []).filter((_, i) => i !== index);

    setValue("images", updated, { shouldValidate: true });
    setImagePreviews(imagePreviews.filter((_, i) => i !== index));
  };

  return (
    <>
      {loader && <Loader />}
      <section className="addPromo position-relative py-3">
        <Container>
          <Row className="justify-content-center">
            <Col lg="12">
              <div className="d-flex align-items-center gap-10">
                <Link
                  to={`/hotel?page=${page}`}
                  className="border d-flex align-items-center p-2 rounded"
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="12"
                    height="12"
                    viewBox="0 0 10 16"
                    fill="none"
                  >
                    <path
                      d="M8.64707 0.473344C8.55514 0.381188 8.44594 0.308072 8.32572 0.258184C8.20549 0.208296 8.07661 0.182617 7.94644 0.182617C7.81628 0.182617 7.68739 0.208296 7.56717 0.258184C7.44694 0.308072 7.33774 0.381188 7.24582 0.473344L0.667065 7.05209C0.593675 7.12533 0.53545 7.21233 0.495723 7.3081C0.455996 7.40387 0.435547 7.50654 0.435547 7.61022C0.435547 7.7139 0.455996 7.81657 0.495723 7.91234C0.53545 8.00811 0.593675 8.0951 0.667065 8.16834L7.24582 14.7471C7.63373 15.135 8.25915 15.135 8.64707 14.7471C9.03498 14.3592 9.03498 13.7338 8.64707 13.3458L2.9154 7.60626L8.65498 1.86668C9.03498 1.48668 9.03498 0.853344 8.64707 0.473344Z"
                      fill="#E8307A"
                      stroke="#E8307A"
                      strokeWidth="0.2"
                    />
                  </svg>
                </Link>
                <h4 className="mb-0 py-3 fw-bold text-dark text-capitalize">
                  {id
                    ? t("manageHotel.editHotel")
                    : t("manageHotel.addNewHotel")}
                </h4>
              </div>
            </Col>

            <Col lg="12">
              <div
                className="formWrpper px-lg-5 p-md-4 p-3 rounded"
                style={{ background: "#EEEEEE" }}
              >
                <Form onSubmit={handleSubmit(submitHandler)}>
                  <Row>
                    <Col lg="6" className="my-2">
                      <label>{t("manageHotel.hotelName")}</label>
                      <input
                        type="text"
                        className="form-control"
                        {...register("hotelName")}
                      />
                      {errors?.hotelName && (
                        <p className="text-danger">
                          {errors.hotelName.message}
                        </p>
                      )}
                    </Col>

                    <Col lg="6" className="my-2">
                      <label>{t("manageHotel.assignAdmin")}</label>
                      <select
                        className="form-control"
                        {...register("userId", {
                          required: "Please select a hotel admin",
                        })}
                      >
                        <option value="">
                          {t("manageHotel.selectAdmin", "select hotel admin")}
                        </option>
                        {hotelAdminData?.ownerList?.map((admin) => (
                          <option key={admin._id} value={admin._id}>
                            {admin.name}
                          </option>
                        ))}
                      </select>

                      {errors?.userId && (
                        <p className="text-danger">{errors.userId.message}</p>
                      )}
                    </Col>

                    <Col lg="6" className="my-2">
                      <label>{t("manageHotel.totalRoom")}</label>
                      <input
                        type="number"
                        className="form-control"
                        min={0}
                        {...register("totalRoom", {
                          valueAsNumber: true,
                          min: 0,
                        })}
                        onKeyDown={(e) => e.key === "-" && e.preventDefault()}
                        onInput={(e) => {
                          if (e.target.value < 0) e.target.value = 0;
                        }}
                      />
                      {errors?.totalRoom && (
                        <p className="text-danger">
                          {errors.totalRoom.message}
                        </p>
                      )}
                    </Col>

                    <Col lg="6" className="my-2">
                      <label>{t("manageHotel.country")}</label>
                      <Controller
                        control={control}
                        name="country"
                        render={({ field }) => (
                          <Select
                            {...field}
                            
                            options={countryOptions}
                            value={countryOptions.find(
                              (c) => c.value === field.value
                            )}
                            onChange={(option) => {
                              setValue("country", option.value, {
                                shouldValidate: true,
                              });
                              setValue("state", "");
                              setValue("city", "");
                            }}
                          />
                        )}
                      />
                    </Col>

                    <Col lg="6" className="my-2">
                      <label>{t("manageHotel.province","Province")} </label>

                      <Controller
                        control={control}
                        name="state"
                        render={({ field }) => (
                          <Select
                            {...field}
                            options={stateOptions(watch("country"))}
                            value={stateOptions(watch("country")).find(
                              (s) => s.value === field.value
                            )}
                            onChange={(option) => {
                              setValue("state", option.value, {
                                shouldValidate: true,
                              });
                              setValue("city", "");
                            }}
                          />
                        )}
                      />
                    </Col>
                    <Col lg="6" className="my-2">
                      <label>{t("manageHotel.cityOrDistrinct","City Or District")}</label>
                      <Controller
                        control={control}
                        name="city"
                        render={({ field }) => (
                          <Select
                            {...field}
                            options={cityOptions(
                              watch("country"),
                              watch("state")
                            )}
                            value={cityOptions(
                              watch("country"),
                              watch("state")
                            ).find((ci) => ci.value === field.value)}
                            onChange={(option) =>
                              setValue("city", option.value, {
                                shouldValidate: true,
                              })
                            }
                          />
                        )}
                      />
                    </Col>

                    <Col lg="6" md="6" className="my-2">
                      <label>{t("manageHotel.address")}</label>
                      <GoogleAutocomplete
                        lat={watch("lat")}
                        lng={watch("lng")}
                        name={"address"}
                        address={watch("address")}
                        onChange={addressChangeHandler}
                      />
                      {errors?.address && (
                        <p className="text-danger">
                          {errors?.address?.message}
                        </p>
                      )}
                    </Col>

                    <Col lg="6" md="6" className="my-2">
                      <label>{t("manageHotel.phone")}</label>
                      <Controller
                        control={control}
                        name="mobileNumber"
                        render={({ field }) => (
                          <PhoneInput
                            {...field}
                            country={"th"}
                            value={field.value}
                            onChange={(value, { dialCode }) => {
                              setValue("countryCode", dialCode);
                              field.onChange(value);
                            }}
                          />
                        )}
                      />
                      {errors?.mobileNumber && (
                        <p className="text-danger">
                          {errors.mobileNumber.message}
                        </p>
                      )}
                    </Col>

                    <Col lg="6" className="my-2">
                      <label>{t("manageHotel.email")}</label>
                      <input
                        type="email"
                        className="form-control"
                        {...register("email")}
                      />
                      {errors?.email && (
                        <p className="text-danger">{errors.email.message}</p>
                      )}
                    </Col>
                    {/* <Col lg="6" className="my-2">
                      <label>{t("manageHotel.checkInTime")}</label>
                      <input
                        type="time"
                        className="form-control"
                        {...register("checkInTime")}
                      />
                      {errors?.checkInTime && (
                        <p className="text-danger">
                          {errors.checkInTime.message}
                        </p>
                      )}
                    </Col>

                    <Col lg="6" className="my-2">
                      <label>{t("manageHotel.checkOutTime")}</label>
                      <input
                        type="time"
                        className="form-control"
                        {...register("checkOutTime")}
                      />
                      {errors?.checkOutTime && (
                        <p className="text-danger">
                          {errors.checkOutTime.message}
                        </p>
                      )}
                    </Col> */}

                    <Col lg="6" className="my-2">
                      <label>{t("manageHotel.assignPin")}</label>
                      <select
                        className="form-control"
                        value={
                          watch("pinStatus")
                            ? JSON.stringify(watch("pinStatus"))
                            : ""
                        }
                        {...register("pinStatus", {
                          required: "Please select a Pin",
                          setValueAs: (val) => {
                            if (!val) return null;
                            try {
                              return JSON.parse(val);
                            } catch {
                              return null;
                            }
                          },
                        })}
                      >
                        {pinListData?.pinList?.map((pin, idx) => (
                          <option key={idx} value={JSON.stringify(pin)}>
                            {pin.label}
                          </option>
                        ))}
                      </select>

                      {errors?.pinStatus && (
                        <p className="text-danger">
                          {errors.pinStatus.message}
                        </p>
                      )}
                    </Col>

                    <Col lg="12" className="my-2">
                      <label>{t("manageHotel.description")}</label>
                      <textarea
                        className="form-control"
                        {...register("description")}
                        rows={3}
                      ></textarea>
                    </Col>

                    <Col lg="12" className="my-2">
                      <label className="fw-semibold">
                        {t("manageHotel.hotelAmenities")}
                      </label>
                      <div
                        className="d-flex flex-wrap gap-3 p-2 border rounded"
                        style={{ maxHeight: "200px", overflowY: "auto" }}
                      >
                        {hotelAmenities?.length > 0
                          ? hotelAmenities.map((amenity) => (
                            <div
                              key={amenity._id}
                              className="d-flex align-items-center"
                              style={{ minWidth: "150px" }}
                            >
                              <Form.Check
                                type="checkbox"
                                label={amenity.name}
                                checked={watch("amenities")?.includes(
                                  amenity._id
                                )}
                                onChange={(e) =>
                                  handleCheckbox(
                                    "amenities",
                                    amenity._id,
                                    e.target.checked
                                  )
                                }
                              />
                            </div>
                          ))
                          : Array.from({ length: 6 }).map((_, idx) => (
                            <div
                              key={idx}
                              className="d-flex align-items-center"
                              style={{ minWidth: "150px" }}
                            >
                              <Placeholder
                                as="div"
                                animation="glow"
                                className="w-100"
                              >
                                <Placeholder xs={8} bg="primary" />
                              </Placeholder>
                            </div>
                          ))}
                      </div>
                    </Col>

                    <Col lg="12" className="my-2">
                      <div className="py-2">
                        <label className="form-label fw-bold text-muted ps-2 m-0">
                          {t("manageHotel.uploadImages")}:
                        </label>

                        <div
                          className="border border-2 border-dashed rounded d-flex flex-column align-items-center justify-content-center p-4 bg-light"
                          style={{ cursor: "pointer", minHeight: "140px" }}
                          onClick={() =>
                            document.getElementById("imageUploadInput").click()
                          }
                        >
                          <i className="bi bi-cloud-upload fs-1 text-muted"></i>
                          <p className="m-0 text-muted">
                            {t("manageHotel.columns.clickOrDrag")}
                          </p>
                          <input
                            id="imageUploadInput"
                            type="file"
                            accept="image/*"
                            multiple
                            className="d-none"
                            onChange={handleImageChange}
                          />
                        </div>

                        {errors?.images && (
                          <p className="text-danger m-0 mt-1">
                            {errors.images.message}
                          </p>
                        )}

                        <div className="d-flex flex-wrap gap-2 mt-3">
                          {imgloader ? (
                            <div
                              style={{ height: 120, width: 120 }}
                              className="d-flex align-items-center justify-content-center border rounded"
                            >
                              <Loading fullSize={true} />
                            </div>
                          ) : (
                            imagePreviews?.map((src, i) => (
                              <div
                                key={i}
                                style={{
                                  width: 120,
                                  height: 120,
                                  position: "relative",
                                }}
                                className="border rounded overflow-hidden shadow-sm"
                              >
                                <img
                                  src={src}
                                  alt={`preview-${i}`}
                                  className="w-100 h-100 object-fit-cover"
                                />
                                <button
                                  type="button"
                                  className="btn btn-sm btn-danger position-absolute top-0 end-0 m-1 rounded-circle"
                                  onClick={() => handleRemoveImage(i)}
                                >
                                  ✕
                                </button>
                              </div>
                            ))
                          )}
                        </div>
                      </div>
                    </Col>

                    <Col lg="12" className="my-3">
                      <div className="d-flex align-items-center gap-3 px-4">
                        <Form.Check
                          type="switch"
                          id="status-switch"
                          label="Status"
                          {...register("status")}
                          style={{ transform: "scale(1.4)", cursor: "pointer" }}
                          className="form-switch-success"
                        />
                      </div>
                    </Col>

                    <Col
                      lg="12"
                      className="my-3 d-flex justify-content-between"
                    >
                      <Link to={`/hotel?page=${page}`}>
                        <Button variant="secondary">
                          {t("manageHotel.columns.back")}
                        </Button>
                      </Link>
                      <Button type="submit" variant="primary">
                        {id
                          ? t("manageHotel.columns.update")
                          : t("manageHotel.columns.submit")}
                      </Button>
                    </Col>
                  </Row>
                </Form>
              </div>
            </Col>
          </Row>
        </Container>
      </section>
    </>
  );
};

export default AddEditHotel;
